import {
  ApolloClient,
  ApolloLink,
  createHttpLink,
  gql,
  InMemoryCache,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { getToken } from '@gsa/afp-shared-ui-utils';
import { CUSTOM_ERROR } from '../utilities/constants';

export const GetGlobalMessagesCacheQuery = gql`
  query GetGlobalMessages {
    messages {
      id
      message
      type
      closeable
    }
  }
`;

const httpLink = createHttpLink({
  uri: window.AFP_CONFIG.api_url,
});

const authLink = setContext(async (_, { headers }) => {
  // get the authentication token from local storage if it exists
  const token = await getToken();
  // return the headers to the context so httpLink can read them
  return {
    headers: {
      ...headers,
      Authorization: token ? `Bearer ${token}` : '',
      'x-datadog-origin': 'rum',
      'x-datadog-sampling-priority': '1',
    },
  };
});

// error codes the global handler below renders a generic system error for
const GLOBAL_ERROR_CODES = [
  'INTERNAL_SERVER_ERROR',
  'GRAPHQL_VALIDATION_FAILED',
];

export const getErrorExtensions = (err) => err?.graphQLErrors?.[0]?.extensions;

// Name of the originating error, used by pages to id their own messages.
// `errorName` is the current contract (@gsa/afp-shared-svc-utils); `exception.name`
// is the pre-Apollo-Server-4 shape, kept until every service is on the new helper.
export const getErrorName = (err) => {
  const extensions = getErrorExtensions(err);
  return extensions?.errorName || extensions?.exception?.name;
};

// Single definition of "this is a business error the page presents itself".
// Every signal the backend has used for this lives here and nowhere else, so
// errorLink and the pages can never disagree about a given error.
const hasCustomErrorMarker = (extensions) =>
  Boolean(
    extensions?.isCustomError ||
    extensions?.errorName === CUSTOM_ERROR ||
    extensions?.code === CUSTOM_ERROR ||
    extensions?.exception?.name ||
    extensions?.exception?.jse_shortmsg ||
    extensions?.exception?.stacktrace?.startsWith(CUSTOM_ERROR),
  );

export const isCustomError = (err) =>
  hasCustomErrorMarker(getErrorExtensions(err));
export const isNonCustomError = (err) => !isCustomError(err);

// True only when the backend has *positively* classified the failure as
// internal, and the generic banner therefore says everything worth saying.
//
// The absence of a marker does not mean "system error", it means "unknown":
// the services do not emit errorName / isCustomError yet, so an unmarked
// INTERNAL_SERVER_ERROR is just as likely to be a business rule. Treating
// unknown as systemic hid real business messages behind the generic banner,
// so a page keeps its own message unless the backend says otherwise.
export const isHandledByGlobalErrorHandler = (err) => {
  const extensions = getErrorExtensions(err);
  if (!extensions || hasCustomErrorMarker(extensions)) return false;
  const classifiedByBackend =
    extensions.isCustomError === false ||
    typeof extensions.errorName === 'string';
  return classifiedByBackend && GLOBAL_ERROR_CODES.includes(extensions.code);
};

// exported so the global-error decision can be exercised directly in tests
export const errorLink = onError(
  ({ graphQLErrors, networkError, operation }) => {
    const { cache } = operation.getContext();
    if (graphQLErrors) {
      graphQLErrors.forEach(({ message, extensions }) => {
        if (
          // only un-handled errors; a 'custom-error' is presented by the page itself
          !hasCustomErrorMarker(extensions) &&
          GLOBAL_ERROR_CODES.includes(extensions?.code)
        ) {
          const m =
            !process.env.NODE_ENV || process.env.NODE_ENV === 'development'
              ? `Internal server error: ${message}`
              : 'There was a system error. Please try again later. If you continue to experience technical difficulties with this page, please contact the GSA Fleet Technical support team at 866-472-1711 or gsafleet@gsa.gov.';
          cache.writeQuery({
            query: GetGlobalMessagesCacheQuery,
            data: {
              messages: [
                {
                  id: 'GLOBAL_ERROR',
                  message: m,
                  type: 'error',
                  closeable: true,
                },
              ],
            },
          });
        }
      });
      // TODO: network error scenarios
    } else if (networkError) {
      const m =
        !process.env.NODE_ENV || process.env.NODE_ENV === 'development'
          ? `Network failure: ${networkError}`
          : 'An error occurred. Please refresh this page. Contact administrator if error persists.';
      cache.writeQuery({
        query: GetGlobalMessagesCacheQuery,
        data: {
          messages: [
            {
              id: 'GLOBAL_ERROR',
              message: m,
              type: 'error',
              closeable: false,
            },
          ],
        },
      });
    }
  },
);

// custom link handler to reset global error messages before next API call
export const initLink = new ApolloLink((operation, forward) => {
  const { cache } = operation.getContext();
  cache.writeQuery({
    query: GetGlobalMessagesCacheQuery,
    data: {
      messages: [
        {
          id: 'GLOBAL_ERROR',
          message: '',
          type: 'error',
          closeable: true,
        },
      ],
    },
  });
  return forward(operation);
});

const dataStore = new ApolloClient({
  link: authLink.concat(initLink).concat(errorLink).concat(httpLink),
  cache: new InMemoryCache(), // removing __typename
});

const sharedPublicLink = createHttpLink({
  uri: window.AFP_CONFIG.public_api_url,
});

export const sharedGatewayClient = new ApolloClient({
  link: initLink.concat(errorLink).concat(sharedPublicLink),
  cache: new InMemoryCache(),
});

// Takes the generic banner down. A page showing its own, more specific message
// calls this so one failure produces one explanation rather than two. Both
// clients write the same GLOBAL_ERROR entry, so clear it on each.
export const clearGlobalErrorMessage = () => {
  const data = {
    messages: [
      { id: 'GLOBAL_ERROR', message: '', type: 'error', closeable: true },
    ],
  };
  [dataStore, sharedGatewayClient].forEach((client) => {
    client.writeQuery({ query: GetGlobalMessagesCacheQuery, data });
  });
};

export default dataStore;
